import BusinessContinuityPlan from '../../../../../../helpers/BusinessContinuityPlan';
import { forEach, get, has, head, isEmpty, keys, map } from 'lodash';
import Loading from '../../../../../../components/Loading';
import { Button, Col, Modal, OverlayTrigger, Popover, Radio, Row } from 'react-bootstrap';
import { FormattedNumber } from 'react-intl';
import { Field, InjectedFormProps } from 'redux-form';
import { IChannel, IProduct, ITraveller, TripType } from '../../../../../../data/api/models';
import { IQuoteActions, IQuoteState } from '../../../../../../services/quote/models';
import { useEffect, useState } from 'react';
import * as Commonmark from 'react-commonmark';
import { FaInfoCircle } from 'react-icons/fa';
import SelectControl from '../../../../../../components/SelectControl';
import { isRequired } from '../../../../../../util/validators';
import { II18nMessages } from '../../../../../../services/branding/models';
import { StartDateSelector } from '../../../QuotePayment/Components/QuoteSummary/components/StartDateSelector';
import { windowSize } from '../../../../../../hooks/windowSize';
import DataLayer from '../../../../../../helpers/DataLayer';
import { MaxTripDurationSelector } from '../../MaxTripDurationSelector';
import React = require('react');
import { RemoveUkRegion } from '../../../../../../helpers/RemoveUkRegion';
import { differenceInDays, format, parse } from 'date-fns';

interface IUpsellProps extends InjectedFormProps {
  startDate: string;
  endDate: string;
  tripType: TripType;
  channel: IChannel;
  travellers: ITraveller[];
  product: IProduct;
  quoteState: IQuoteState;
  region: string;
  recalculate: () => any;
  i18n: II18nMessages;
  maxTripDuration: any;
  valid: boolean;
  change: any;
  actions: {
    quote: IQuoteActions;
  }

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

export const Upsell = ({
                         startDate,
                         endDate,
                         tripType,
                         region,
                         channel,
                         travellers,
                         product,
                         quoteState,
                         recalculate,
                         renderDefinition,
                         maxTripDuration,
                         i18n,
                         valid,
                         change,
                         actions,
                         ...props
                       }: IUpsellProps) => {

  const [showModal, setShowModal] = useState(false);
  const [selectedSchemeId, setSelectedSchemeId] = useState(null);
  const [disableUpsell, setDisableUpsell] = useState(true);
  const quotes = get(quoteState, 'quotes');
  const isMobile = windowSize(480).isMobile;

  useEffect(() => {
    setDisableUpsell(!(startDate && region && maxTripDuration));
  }, [startDate, region, maxTripDuration]);

  const checkHasProperty = (quote, check) => quote && quote[check] && quote[check].length > 0;
  const annual = 'Annual Multi Trip';

  const shouldShowAnnualQuotes = (): boolean => {

    const start = parse(startDate, 'dd/MM/yyyy', new Date());
    const end = parse(endDate, 'dd/MM/yyyy', new Date());
    const diff = differenceInDays(end, start);
    const singleTripDuration = diff + 1;
    const hasExceedMaxUpsellDuration = singleTripDuration > 31;
    const isSingle = tripType === 'single';

    const bcp = channel.bcp || null;
    const isAnnualTripAllowed = BusinessContinuityPlan.isSchemeTypeAllowed('annual', bcp);

    let hasAnnualExclusion = false;
    let hideAnnualTrip = false;
    let hideAnnualTripNegatives = false;

    forEach(travellers, (traveller) => {
      if (tripType === 'single' && (get(traveller, 'screening.meta.hasAnnualExclusion', false) === true)) {
        hasAnnualExclusion = true;
      }
    });

    forEach(quotes, (tripTypes) => {
      if (!tripTypes || !has(tripTypes, ['Annual Multi Trip'])) {
        return false;
      }

      const singleTripQuote = get(tripTypes, 'Single Trip', null);
      const annualTripQuote = get(tripTypes, 'Annual Multi Trip', null);

      if (!annualTripQuote) {
        return false;
      }

      const errorTypes = ['errors', 'declines', 'referrals'];
      hideAnnualTrip = errorTypes.some((errorType) => {
        return isSingle && annualTripQuote && (checkHasProperty(annualTripQuote, errorType)
          || annualTripQuote.gross < singleTripQuote.gross);
      });

      if (isSingle && annualTripQuote && annualTripQuote.gross <= singleTripQuote.gross) {
        hideAnnualTripNegatives = true;
      }

    });

    return isAnnualTripAllowed
      && tripType === 'single'
      && !hasExceedMaxUpsellDuration
      && !hasAnnualExclusion
      && !hideAnnualTrip
      && !hideAnnualTripNegatives;
  }

  const renderAnnualQuotes = () => {
    if (!shouldShowAnnualQuotes()) {
      return null;
    }

    if (isEmpty(quotes) || !product || quoteState.isCalculating) {
      return (
        <Loading/>
      )
    }

    const orderedQuotes = map(product.metadata.scheme_order, (order: string) => {
      return quotes[order];
    });

    const annualQuotes = map(orderedQuotes[0] && orderedQuotes, (tripTypes, level) => {
      if (has(tripTypes, [annual])) {
        return tripTypes[annual]
      }
    }).filter(item => item)

    if (isEmpty(annualQuotes)) {
      return (
        <Loading/>
      )
    }

    return (
      <>
        <div className={'scheme-selection-container'}>
          <div className={'scheme-choice-header-title'}>Upgrade to Annual Multi-Trip</div>
          {map(annualQuotes, (quote, index) => {
            const image = require('../../../../img/' + get(quote, 'scheme.coverLevel') + '-level.svg')
            return (
              <div className={`scheme-choice-cover-levels`}><img src={image} alt="Admiral"/></div>
            )
          })}
        </div>

        <div className={'scheme-selection-container'}>
          <div className={'scheme-choice-header-title sub'}>Insurance Product Information Document</div>
          {map(annualQuotes, (quote, index) => {
            const documents = get(quote, 'scheme.documents');

            return (
              <div className={`scheme-choice-cover-levels`} key={index}>
                {map(documents, (documentUrl: string, documentName: string) => {
                  if (!documentUrl || documentName !== 'Insurance Product Information Document') {
                    return null;
                  }

                  return (
                    <div>
                      <a className={'scheme-document-link'} href={documentUrl} target="_blank" key={documentName}>
                        <img src={require('../../../../img/document.svg')} alt={'Admiral'}/>
                        <div>OPEN</div>
                      </a>
                    </div>
                  );
                })}
              </div>
            )
          })}
        </div>

        <div className={'scheme-selection-container'}>
          <div className={'scheme-choice-header-title'}/>
          {map(orderedQuotes[0] && orderedQuotes, (tripTypes, index) => {
            const single = keys(tripTypes).length > 1 && 'Single Trip';
            const difference = (keys(tripTypes).length > 1 ? tripTypes[annual].gross - tripTypes[single].gross : 0);
            return (
              <div className={`scheme-choice-cover-levels`} key={index}>
                <div>

                  {isMobile && (
                    <>
                      <div className={'scheme-choice-mobile-radio'}>
                        <Radio onChange={handleUpsell(tripTypes)}/>
                        <div className={'scheme-price-container'}>
                          <div>Upgrade</div>
                          {'+ '}
                          <FormattedNumber
                            value={difference}
                            style="currency"
                            currency={get(tripTypes, `[${annual}]currency`, '')}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {!isMobile && (
                    <Button
                      onClick={handleUpsell(tripTypes)}
                      className={'admiral-white-btn'}>
                      <div>UPGRADE</div>
                      <div className={'scheme-price-container'}>
                        {'+ '}
                        <FormattedNumber
                          value={difference}
                          style="currency"
                          currency={get(tripTypes, `[${annual}]currency`, '')}
                        />
                      </div>
                    </Button>
                  )}
                </div>
                <div style={{ paddingTop: '15px' }} className={'scheme-price-container'}>
                  Total: {'  '}
                  <FormattedNumber
                    value={tripTypes ? get(tripTypes, `[${annual}]gross`) : 0}
                    style="currency"
                    currency={get(tripTypes, `[${annual}]currency`, '')}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </>
    )
  }

  const renderUpSellModal = () => {

    const hideUKRegion = get(head(props?.premiums), 'information.hideUkRegion', false)
    const productRegions = RemoveUkRegion(product.metadata.regions, props?.quoteForEdit, hideUKRegion)
    const hasUKRegion = get(productRegions, 'uk', null) !== null;

    const popoverClickRootClose = (
      <Popover id="popover-trigger-click-root-close" title="Regions">
        {hasUKRegion && <Commonmark source={product.metadata.definitions.region_uk}/>}
        <Commonmark source={product.metadata.definitions.region_europe}/>
        <Commonmark source={product.metadata.definitions.region_worldwide_excluding}/>
        <Commonmark source={product.metadata.definitions.region_worldwide}/>
      </Popover>
    );

    const regionsList = map(productRegions, (name, key) => ({
      label: name,
      value: key,
    }));

    return (
      <Modal show={showModal} onHide={handleCancelUpsell} keyboard={false} backdrop="static">
        <Modal.Header closeButton={true}>
          <Modal.Title>Upgrading to Annual Multi-Trip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div>
              <div>
                Please confirm the region that you require cover for.
                If your selected region differs from the region of your original
                destination, this may affect the premium:
              </div>
              <div>
                <div className={'region-select-container'}>
                  <div className={'flex--50'}>
                    {i18n.quote.tripDetails.region}{' '}
                    <OverlayTrigger
                      trigger="click"
                      rootClose={true}
                      placement="right"
                      overlay={popoverClickRootClose}
                    >
                      <FaInfoCircle className="info-btn"/>
                    </OverlayTrigger>
                  </div>
                  <div className={'flex--stretch'}>
                    <Field
                      name="region"
                      type="text"
                      component={SelectControl}
                      options={regionsList}
                      validate={[
                        isRequired,
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>

            <StartDateSelector
              tripType={tripType}
              renderDefinition={renderDefinition}
              change={change}
              isUpsell={true}
              startDate={startDate}
            />

            <MaxTripDurationSelector
              product={product}
              valid={valid}
              quoteState={quoteState}
              {...props}
            />

            <div className="important-inline-section" style={{ marginBottom: '20px' }}>
              <div className="important" style={{ padding: 0 }}>
                <strong>IMPORTANT</strong> - {i18n.quote.tripDetails.coverWarning}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Row>
            <Col xs={6}>
              <Button
                className={'admiral-white-btn'}
                block={true}
                id="cancel"
                onClick={handleCancelUpsell}
              >
                CANCEL
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                className={'admiral-primary-btn'}
                block={true}
                id="continue"
                disabled={disableUpsell}
                onClick={handleSubmitUpsell}
              >
                CONTINUE
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    );
  }

  const handleCancelUpsell = () => {
    Promise.all([
      Promise.resolve(change('region', '')),
    ]).then(() => {
      setShowModal(false)
      recalculate()
    });
  }

  const handleSubmitUpsell = () => {
    const shouldUpdateApplication = tripType === 'annual';
    Promise.all([
      Promise.resolve(change('endDate', '')),
      Promise.resolve(change('destinations', [])),
      Promise.resolve(change('tripType', 'annual')),
      Promise.resolve(change('schemeId', selectedSchemeId)),
      Promise.resolve(change('selectedSchemeId', selectedSchemeId)),
    ]).then(() => {
      setShowModal(false)
      Promise.resolve(change(`upsellDate`, format(new Date(), 'yyyy-MM-dd HH:mm:ss'))).then(() => {
        Promise.resolve(actions.quote.calculate(null, true, true, shouldUpdateApplication)).then(() => {
          DataLayer.push({ 'UpgradeToAmt': 'true' })
          recalculate()
        })
      });
    });
  }

  const handleUpsell = (value) => () => {
    const schemeId = get(value, `[${annual}]scheme.id`, '')
    setSelectedSchemeId(schemeId)
    if (schemeId) {
      setShowModal(true);
    }
  }

  return (
    <>
      {renderAnnualQuotes()}
      {renderUpSellModal()}
    </>
  )
}
